import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { CreditBalanceCard } from "./CreditBalanceCard";
import { CreditTransactionsTable } from "./CreditTransactionsTable";
import { RechargeDialog, toastRecharge, type RechargeResponse } from "./RechargeDialog";
import { AdjustDialog } from "./AdjustDialog";
import type { CompanyCreditTransactionsResponse } from "@/types";

const PAGE_SIZE = 25;

export function CreditSection({
  companyId,
  balance,
}: {
  companyId: string;
  balance: number;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["admin", "company", companyId, "credit-transactions", page],
    queryFn: async () => {
      const res = await fetch(
        `/api/companies/${companyId}/credit/transactions?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`
      );
      await throwIfNotOk(res, "Greška pri učitavanju historije");
      return res.json() as Promise<CompanyCreditTransactionsResponse>;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, "credit-transactions"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
  };

  const recharge = useMutation({
    mutationFn: async (input: { amount: number; note?: string }) => {
      const res = await fetch(`/api/companies/${companyId}/credit/recharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      await throwIfNotOk(res, "Greška pri dopuni");
      return res.json() as Promise<RechargeResponse>;
    },
    onSuccess: (data) => {
      toastRecharge(data, "Kredit dopunjen");
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, "invoices"] });
      setRechargeOpen(false);
    },
  });

  const adjust = useMutation({
    mutationFn: async (input: { amount: number; note: string }) => {
      const res = await fetch(`/api/companies/${companyId}/credit/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      await throwIfNotOk(res, "Greška pri korekciji");
      return res.json();
    },
    onSuccess: () => {
      toast.success("Korekcija primijenjena");
      invalidate();
      setAdjustOpen(false);
    },
  });

  return (
    <div className="space-y-4">
      <CreditBalanceCard
        balance={balance}
        onRecharge={() => setRechargeOpen(true)}
        onAdjust={() => setAdjustOpen(true)}
      />
      <CreditTransactionsTable
        invoiceUrl={(txId) =>
          `/api/admin/companies/${companyId}/invoice?transactionId=${txId}`
        }
        items={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
      <RechargeDialog
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        onSubmit={(d) => recharge.mutate(d)}
        isPending={recharge.isPending}
      />
      <AdjustDialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSubmit={(d) => adjust.mutate(d)}
        isPending={adjust.isPending}
      />
    </div>
  );
}
